import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '@core/authentication/services/auth.service';
import { HEADER_SKIP_INTERCEPTOR } from '@shared/tools/constants';
import { CommerceStorageContext, getCommerceStorageContext, getCommerceStorageKeys } from '@shared/tools/storage-keys';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private authService: AuthService,
  ) {}

  intercept(req: HttpRequest<unknown>, handler: HttpHandler): Observable<HttpEvent<unknown>> {
    // 1. Ignore interceptor if it has custom header (X-Skip-Interceptor)
    if (req.headers.has(HEADER_SKIP_INTERCEPTOR)) {
      return handler.handle(req.clone({ headers: req.headers.delete(HEADER_SKIP_INTERCEPTOR) }));
    }

    let authReq = req;
    const authContext = getCommerceStorageContext();
    const token = this.authService.getAccessToken(authContext);
    if (token) {
      authReq = this.addTokenHeader(req, token);
    }

    authReq = this.addLanguageHeader(authReq);

    return handler.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(authReq, handler, authContext);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    authContext: CommerceStorageContext,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.tryRefreshToken(authContext).pipe(
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);

          // Retry original request with new token
          return next.handle(this.addTokenHeader(request, newToken));
        }),
        catchError(() => {
          this.isRefreshing = false;

          this.authService.clearAuth(authContext);

          if (authContext === 'asm') {
            const currentPath = window.location.pathname;
            if (!currentPath.startsWith('/agent/login')) {
              localStorage.setItem(
                getCommerceStorageKeys(authContext).redirectAfterLogin,
                currentPath + window.location.search + window.location.hash,
              );
            }
            void this.router.navigate(['/agent/login']);
          } else {
            void this.router.navigate(['/login']);
          }

          return throwError(() => new Error('Session expired'));
        }),
      );
    } else {
      // If it's already refreshing, wait until the new token arrives
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap(token => next.handle(this.addTokenHeader(request, token))),
      );
    }
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  /**
   * Adds the Accept-Language header with a normalized language code.
   * Extracts the browser language and normalizes it to 'pt' or 'en'.
   */
  private addLanguageHeader(request: HttpRequest<any>): HttpRequest<any> {
    const browserLang = navigator.language || 'pt-BR';
    const normalizedLang = this.normalizeLanguage(browserLang);

    return request.clone({
      headers: request.headers.set('Accept-Language', normalizedLang),
    });
  }

  /**
   * Normalizes the browser language to a supported language code.
   * @param browserLang The browser language (e.g., 'pt-BR', 'en-US')
   * @returns Normalized language code ('pt' or 'en')
   */
  private normalizeLanguage(browserLang: string): string {
    const lang = browserLang.split('-')[0].toLowerCase();
    const supportedLanguages = ['pt', 'en'];
    return supportedLanguages.includes(lang) ? lang : 'pt';
  }
}
